<template>
	<BaseLayout
		background-color-class="bg-color-white"
		:footer-full="true"
		:include-padding="!displayContactBanner"
		:content-full-height="!displayContactBanner"
	>
		<div
			:class="{
				'help-center-landing-page-content pb-600 pb-800-smo': displayContactBanner
			}"
		>
			<section>
				<div class="container">
					<h1 class="heading-2 text-align-center">How can we help&nbsp;you?</h1>
					<div class="grid-md gutters-300-md">
						<div class="col-8-md mt-200 col-offset-2-md">
							<HelpCenterSearchBar :placeholder="'Search the help center'" />
						</div>
					</div>
				</div>
			</section>

			<section class="pt-300">
				<div class="container">
					<transition name="fade">
						<div v-if="!loading" class="grid-md grid-3-up-md gutters-300-md homepage-grid">
							<div v-for="category in categories" :key="category.id" class="col pb-200">
								<HelpCenterCategoryTile :category="category" />
							</div>
						</div>
					</transition>
				</div>
			</section>
			<HelpCenterContactBanner
				v-if="displayContactBanner"
				class="help-center-contact-banner"
				data-test="help-center-contact-banner"
			/>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref, Ref } from 'vue';
import { getHelpCenterCategories } from '@api/help-center';
import { HelpCenterCategory } from 'types/help-center';
import HelpCenterCategoryTile from '@components/help-center/help-center-category-tile.vue';
import HelpCenterContactBanner from '@components/help-center/help-center-contact-banner.vue';
import HelpCenterSearchBar from '@components/help-center/help-center-search-bar.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@stores/user';

const categories: Ref<Array<HelpCenterCategory>> = ref([]);
const loading = ref(true);
const appStore = useAppStore();
const userStore = useUserStore();
const route = useRoute();
const displayContactBanner = computed((): boolean => appStore.isAuthenticated && userStore.isExistingInvestor);

async function setup(): Promise<void> {
	if (route.query.notFound) {
		useHead({
			title: 'Help Center',
			meta: [
				{ property: 'og:title', content: 'Help Center' },
				{
					name: 'prerender-status-code',
					content: '404'
				}
			],
			script: [`window.prerenderReady = true;`]
		});
	} else {
		useHead({
			title: 'Help Center',
			meta: [{ property: 'og:title', content: 'Help Center' }]
		});
	}

	try {
		categories.value = await getHelpCenterCategories();
		loading.value = false;
	} catch {}
}
setup();
</script>
<style lang="scss" scoped>
.help-center-landing-page-content {
	padding-top: 3rem;
	min-height: calc(100vh - 48px);
	position: relative;
}

@media screen and (min-width: 1200px) {
	.help-center-landing-page-content {
		min-height: calc(100vh - 64px);
	}
}

@media screen and (max-width: 767px) {
	.pb-800-smo {
		padding-bottom: 8rem;
	}
}

.help-center-contact-banner {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	margin-bottom: -1px;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}
.label-tooltip {
	text-transform: none;
}
</style>
